import { useState, useEffect, useCallback } from 'react'
import { TagService } from '../services/tags'
import { CategoryService } from '../services/categories'
import { AuthService } from '../services/auth'
import type { Tag } from '../services/tags'

interface TagFormData {
  name: string
  slug: string
  description: string
  category_id: string
  is_active: boolean
}

const TagManager = () => {
  const [tags, setTags] = useState<Tag[]>([])
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [formData, setFormData] = useState<TagFormData>({
    name: '',
    slug: '',
    description: '',
    category_id: '',
    is_active: true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [draggedTag, setDraggedTag] = useState<Tag | null>(null)
  const [showInactive, setShowInactive] = useState(false)
  const [showMigrationModal, setShowMigrationModal] = useState(false)

  const loadTags = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await TagService.getTags(showInactive)
      setTags(data)
    } catch (error) {
      console.error('Failed to load tags:', error)
      setError('タグの読み込みに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }, [showInactive])

  const loadCategories = useCallback(async () => {
    try {
      const data = await CategoryService.getCategories()
      setCategories(data)
    } catch (error) {
      console.error('Failed to load categories:', error)
      setError('カテゴリーの読み込みに失敗しました')
    }
  }, [])

  useEffect(() => {
    loadTags()
    loadCategories()
  }, [loadTags, loadCategories])

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      category_id: '',
      is_active: true
    })
    setEditingTag(null)
    setError('')
    setSuccess('')
  }

  const handleAddTag = () => {
    resetForm()
    setShowAddForm(true)
  }

  const handleEditTag = (tag: Tag) => {
    setFormData({
      name: tag.name,
      slug: tag.slug,
      description: tag.description || '',
      category_id: tag.category_id,
      is_active: tag.is_active
    })
    setEditingTag(tag)
    setShowAddForm(true)
  }

  const handleGenerateSlug = async () => {
    if (!formData.name.trim()) {
      setError('タグ名を入力してください')
      return
    }

    try {
      const slug = await TagService.generateUniqueSlug(formData.name)
      setFormData({ ...formData, slug })
    } catch (error) {
      console.error('Failed to generate slug:', error)
      setError('スラッグの生成に失敗しました')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    setSuccess('')

    try {
      // Check if tag name is unique
      const isUnique = await TagService.isTagNameUnique(
        formData.name,
        editingTag?.id
      )

      if (!isUnique) {
        setError('このタグ名は既に使用されています')
        return
      }

      if (editingTag) {
        // Update existing tag
        await TagService.updateTag(editingTag.id, formData)
        
        await AuthService.logAdminAction('update_tag', 'tag', editingTag.id, {
          name: formData.name,
          slug: formData.slug
        })
        
        setSuccess('タグを更新しました')
      } else {
        // Create new tag
        const newTag = await TagService.createTag(formData)
        
        await AuthService.logAdminAction('create_tag', 'tag', newTag.id, {
          name: formData.name,
          slug: formData.slug
        })
        
        setSuccess('タグを作成しました')
      }

      setShowAddForm(false)
      resetForm()
      await loadTags()
    } catch (err) {
      console.error('Failed to save tag:', err)
      setError(err instanceof Error ? err.message : 'タグの保存に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteTag = async (tag: Tag) => {
    if (confirm(`「${tag.name}」を削除してもよろしいですか？\n関連する動画との紐付けも削除されます。`)) {
      try {
        setIsLoading(true)
        await TagService.deleteTag(tag.id)
        
        await AuthService.logAdminAction('delete_tag', 'tag', tag.id, {
          name: tag.name
        })
        
        setSuccess(`タグ「${tag.name}」を削除しました`)
        await loadTags()
      } catch (error: unknown) {
        console.error('Failed to delete tag:', error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        setError(errorMessage || 'タグの削除に失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleToggleActive = async (tag: Tag) => {
    try {
      setIsLoading(true)
      await TagService.updateTag(tag.id, { is_active: !tag.is_active })
      
      await AuthService.logAdminAction('toggle_tag_active', 'tag', tag.id, {
        name: tag.name,
        is_active: !tag.is_active
      })
      
      await loadTags()
    } catch (error) {
      console.error('Failed to toggle tag active status:', error)
      setError('タグの状態更新に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDragStart = (tag: Tag) => {
    setDraggedTag(tag)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = async (e: React.DragEvent, dropTag: Tag) => {
    e.preventDefault()
    
    if (!draggedTag || draggedTag.id === dropTag.id) return

    try {
      setIsLoading(true)
      
      const draggedIndex = tags.findIndex(t => t.id === draggedTag.id)
      const dropIndex = tags.findIndex(t => t.id === dropTag.id)
      
      const newTags = [...tags]
      newTags.splice(draggedIndex, 1)
      newTags.splice(dropIndex, 0, draggedTag)
      
      // Update display orders
      const updates = newTags.map((tag, index) => ({
        id: tag.id,
        display_order: index + 1
      }))
      
      await TagService.reorderTags(updates)
      
      await AuthService.logAdminAction('reorder_tags', 'tag', draggedTag.id, {
        from: draggedIndex + 1,
        to: dropIndex + 1
      })
      
      setTags(newTags)
    } catch (error) {
      console.error('Failed to reorder tags:', error)
      setError('タグの並び替えに失敗しました')
      await loadTags()
    } finally {
      setIsLoading(false)
      setDraggedTag(null)
    }
  }

  const handleMigration = async () => {
    if (!confirm('既存の動画のタグを新しいタグシステムに移行しますか？')) {
      return
    }

    try {
      setIsLoading(true)
      const result = await TagService.migrateInlineTags()
      
      await AuthService.logAdminAction('migrate_tags', 'system', undefined, result)
      
      setSuccess(`移行完了: ${result.createdTags}個のタグを作成し、${result.updatedVideos}個の動画を更新しました`)
      setShowMigrationModal(false)
      await loadTags()
    } catch (error) {
      console.error('Failed to migrate tags:', error)
      setError('タグの移行に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">タグ管理</h2>
          <p className="text-sm text-gray-600">動画に付けるタグを管理します</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowMigrationModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            タグを移行
          </button>
          <button
            onClick={handleAddTag}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            新しいタグを追加
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}
      
      {error && !showAddForm && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filter Options */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="ml-2 text-sm text-gray-600">無効なタグも表示</span>
        </label>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-base font-medium text-gray-900 mb-4">
            {editingTag ? 'タグを編集' : '新しいタグを追加'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  タグ名 *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="例: 起業, マーケティング"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  カテゴリー *
                </label>
                <select
                  required
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">カテゴリーを選択</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スラッグ *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: kigyo, marketing"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateSlug}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-2 rounded-md text-sm"
                  >
                    自動生成
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  URLに使用される識別子です
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                説明
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="このタグの説明（オプション）"
              />
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">有効</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                無効にすると、動画への新規割り当てができなくなります
              </p>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false)
                  resetForm()
                }}
                disabled={isLoading}
                className="bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {isLoading ? '保存中...' : editingTag ? '更新' : '作成'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Migration Modal */}
      {showMigrationModal && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-base font-medium text-gray-900 mb-4">
            タグシステムの移行
          </h3>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              この操作は既存の動画に設定されているインラインタグを新しいタグシステムに移行します。
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-yellow-800 mb-2">移行内容:</h4>
              <ul className="text-xs text-yellow-700 space-y-1">
                <li>• 動画の既存タグから一意のタグを抽出</li>
                <li>• 新しいタグテーブルにタグを作成</li>
                <li>• 動画とタグの関連付けを更新</li>
                <li>• 既存のタグ名は保持されます</li>
              </ul>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowMigrationModal(false)}
                disabled={isLoading}
                className="bg-gray-300 hover:bg-gray-400 disabled:opacity-50 text-gray-700 px-4 py-2 rounded-md text-sm font-medium"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleMigration}
                disabled={isLoading}
                className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {isLoading ? '移行中...' : '移行を実行'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tags List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-medium text-gray-900">
            タグ一覧 ({tags.length}件)
          </h3>
        </div>
        
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">読み込み中...</p>
          </div>
        ) : tags.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {tags.map((tag) => (
              <div
                key={tag.id}
                draggable
                onDragStart={() => handleDragStart(tag)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, tag)}
                className={`p-4 hover:bg-gray-50 cursor-move ${
                  draggedTag?.id === tag.id ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {tag.name}
                        {!tag.is_active && (
                          <span className="ml-2 text-xs text-gray-500">（無効）</span>
                        )}
                      </h4>
                      <p className="text-xs text-gray-500">
                        カテゴリー: {'categories' in tag && tag.categories ? (tag.categories as {name: string}).name : 'N/A'} • スラッグ: {tag.slug}
                        {tag.description && ` • ${tag.description}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      順序: {tag.display_order}
                    </span>
                    <button
                      onClick={() => handleToggleActive(tag)}
                      className={`text-xs font-medium ${
                        tag.is_active
                          ? 'text-green-600 hover:text-green-500'
                          : 'text-gray-600 hover:text-gray-500'
                      }`}
                    >
                      {tag.is_active ? '有効' : '無効'}
                    </button>
                    <button
                      onClick={() => handleEditTag(tag)}
                      className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDeleteTag(tag)}
                      className="text-red-600 hover:text-red-500 text-sm font-medium"
                    >
                      削除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500">タグがありません</p>
            <button
              onClick={handleAddTag}
              className="mt-2 text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              最初のタグを追加
            </button>
          </div>
        )}
      </div>

      <div className="text-xs text-gray-500 text-center">
        ドラッグ＆ドロップで順序を変更できます
      </div>
    </div>
  )
}

export default TagManager